import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { components } from 'react-select'

import { getLocalStorageData, STORAGE_KEY } from 'services/localStorage.service'
import { CityDTO, StreetDTO } from 'services/yandexApi/types'
import { useStore } from 'store/useStore'
import { OrderKeys } from 'sections/LKSection/modules/OrderModule/data'
import { OrderLocationViewModel } from 'sections/LKSection/modules/OrderModule/stores/models/OrderLocationViewModel'
import { getCreateOrderPageKey } from 'sections/LKSection/modules/OrderModule/utils'
import { ReactSelect } from 'components/ui'
import { FormItem, Tooltip } from 'components/uiKit'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { usePickPointLocation } from 'utils/hooks/usePickPointLocation'
import { Country } from 'shared/types/types'

type LocationFormType = {
  cityItem: CityDTO | null
  streetItem: StreetDTO | null
}

type OrderAddressProps = {
  uiStore: OrderLocationViewModel
  country: Country
  isCreation: boolean
  disabled?: boolean
}

export const OrderAddress = observer(
  ({ uiStore, country, isCreation = false, disabled = false }: OrderAddressProps) => {
    const { appStore, viewOrderUiStore } = useStore()
    const { t } = useTranslation()

    const { control, setValue, watch } = useForm<LocationFormType>({
      mode: 'onChange',
      defaultValues: {
        cityItem: uiStore.cityItem,
        streetItem: uiStore.streetItem,
      },
    })

    const {
      isCityLoading,
      isStreetLoading,
      searchCity,
      searchStreet,
      cityChangeHandler,
      streetChangeHandler,
      getAddressFromCoordinates,
    } = usePickPointLocation(uiStore)

    const { latitude, longitude } = useParams()

    const cityItem = watch('cityItem')

    useEffect(() => {
      if (!isCreation && viewOrderUiStore.initialized) {
        setValue('cityItem', uiStore.cityItem)
        setValue('streetItem', uiStore.streetItem)
      }
    }, [viewOrderUiStore.initialized])

    useAsyncEffect(async () => {
      if (isCreation) {
        const coordinatesFromLS = getLocalStorageData(STORAGE_KEY.COORDINATES)
        const lat = latitude || coordinatesFromLS?.latitude
        const lon = longitude || coordinatesFromLS?.longitude

        if (lat && lon && !uiStore.cityItem && !uiStore.streetItem) {
          uiStore.setCoordinates({ latitude: lat, longitude: lon })

          await getAddressFromCoordinates(`${lon} ${lat}`, 'both', country)
          setValue('cityItem', uiStore.cityItem)
          setValue('streetItem', uiStore.streetItem)
        }
      }
    }, [])

    const handleCityChange = (item: CityDTO) => {
      uiStore.resetAddress()
      uiStore.resetSubsidy()
      setValue('streetItem', null)
      cityChangeHandler(item as CityDTO)
    }

    const handleStreetChange = async (item: StreetDTO) => {
      uiStore.resetSubsidy()
      streetChangeHandler(item as StreetDTO)

      if (item) {
        await uiStore.getSubsidy(5)
      }
    }

    const getKey = (key: string) => `${OrderKeys.locationStep}.${key}`

    return (
      <>
        <Tooltip title={uiStore.cityItem?.formattedAddress}>
          <FormItem label={t(getKey('Город / населенный пункт'))}>
            <Controller<LocationFormType>
              name={'cityItem'}
              control={control}
              render={({ field }) => (
                <ReactSelect
                  async
                  value={field.value as CityDTO}
                  options={uiStore.cityOptions}
                  isLoading={isCityLoading}
                  placeholder={t(getCreateOrderPageKey('Начните вводить'))}
                  cacheOptions
                  isSearchable
                  isClearable
                  isDisabled={!isCreation || disabled}
                  getOptionLabel={(item) => `${(item as CityDTO)?.formattedAddress}`}
                  loadOptions={(query, callback) => searchCity(query, callback, appStore.companyCountry)}
                  onChange={(item) => {
                    handleCityChange(item as CityDTO)
                    field.onChange(item as CityDTO)
                  }}
                  className="cityItem"
                  components={{ Input: CustomInputForCity, Option: CustomOptionForCity }}
                />
              )}
            />
          </FormItem>
        </Tooltip>

        <Tooltip title={uiStore.streetItem?.name || uiStore.streetHouse}>
          <FormItem label={t(getKey('Улица, дом'))}>
            <Controller<LocationFormType>
              name={'streetItem'}
              control={control}
              render={({ field }) => (
                <ReactSelect
                  async
                  value={field.value as StreetDTO}
                  options={uiStore.streetOptions}
                  isLoading={isStreetLoading}
                  placeholder={t(getCreateOrderPageKey('Начните вводить'))}
                  isDisabled={!isCreation || !cityItem || disabled}
                  cacheOptions
                  isSearchable
                  isClearable
                  getOptionLabel={(item) => `${(item as StreetDTO)?.name || uiStore.streetHouse}`}
                  loadOptions={(query, callback) => searchStreet(query, callback)}
                  onChange={(item) => {
                    handleStreetChange(item as StreetDTO)
                    field.onChange(item as StreetDTO)
                  }}
                  className="streetItem"
                  components={{
                    Input: CustomInputForStreet,
                    Option: (props) => CustomOptionForStreet(props, uiStore.streetHouse),
                  }}
                />
              )}
            />
          </FormItem>
        </Tooltip>
      </>
    )
  },
)

/* Данные для автотестов */

const CustomOptionForCity = (props: any) => {
  return (
    <components.Option {...props} className={props.data.className}>
      {props.data.formattedAddress}
    </components.Option>
  )
}

const CustomOptionForStreet = (props: any, streetHouse: string) => {
  return (
    <components.Option {...props} className={props.data.className}>
      {props.data.name || streetHouse}
    </components.Option>
  )
}

const CustomInputForCity = (props: any) => {
  return <components.Input {...props} className={'cityItemInput'} />
}

const CustomInputForStreet = (props: any) => {
  return <components.Input {...props} className={'streetItemInput'} />
}
